import { Link, useParams, useSearchParams } from "react-router";
import { ArrowRight, Check, FileText, Paperclip } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormSubmission } from "./use-form-submission";

// Post-submit confirmation page. Replaces the inline success card the
// legacy FormRenderer renders. URL: /portal/forms/:slug/submitted?submissionId=<id>
//
// Renders the submission timestamp, a flat read-only summary of the
// submitted answers (paired field label → value), any attachments
// uploaded, and a "Submit another response" link back to the form.
//
// Auto-accept submissions never land here — they navigate to
// /portal/dashboard?submitted=deal|lead instead (matches legacy redirect).

export function FormSubmittedPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get("submissionId");
  const query = useFormSubmission(slug, submissionId);

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border bg-card p-8 text-center">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          Submission not found
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We couldn’t find that submission. It may have been removed, or the
          link may be stale.
        </p>
        <Link
          to="/portal/forms"
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          Back to forms
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  const submission = query.data;
  const fields = submission.formDefinition.sections.flatMap((s) => s.fields);
  const submittedAt = new Date(submission.submittedAt);

  return (
    <div>
      <div className="mx-auto mb-6 max-w-2xl rounded-xl border bg-card p-6 text-center">
        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-status-active/15 text-status-active">
          <Check className="h-5 w-5" />
        </div>
        <h1 className="font-display text-xl font-semibold tracking-tight">
          {submission.formName} submitted
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Reference{" "}
          <code className="font-mono text-xs text-foreground">
            {submission.submissionId.slice(0, 8).toUpperCase()}
          </code>{" "}
          ·{" "}
          {submittedAt.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks — we&apos;ve received your submission and will respond shortly.
        </p>
        <Link
          to={`/portal/forms/${slug}`}
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          Submit another response
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="mx-auto max-w-2xl space-y-6">
        <section className="rounded-xl border bg-card p-5">
          <h2 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Your answers
          </h2>
          <dl className="space-y-3">
            {fields
              .filter((f) => f.kind !== "section_heading" && f.kind !== "file")
              .map((field) => {
                const raw = submission.payload[field.name];
                return (
                  <div key={field.id} className="flex flex-col gap-0.5">
                    <dt className="text-xs font-medium text-muted-foreground">
                      {field.label}
                    </dt>
                    <dd className="text-sm text-foreground">
                      {formatValue(raw)}
                    </dd>
                  </div>
                );
              })}
          </dl>
        </section>

        {submission.attachments.length > 0 ? (
          <section className="rounded-xl border bg-card p-5">
            <h2 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Attachments
            </h2>
            <ul className="space-y-2">
              {submission.attachments.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm"
                >
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate font-medium text-foreground">
                    {a.file_name}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {formatBytes(a.size_bytes)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="rounded-xl border bg-muted/30 p-5 text-center">
          <FileText className="mx-auto h-5 w-5 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            Bookboost will review and respond to your submission. You&apos;ll be
            notified by email when there&apos;s an update.
          </p>
        </section>
      </div>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") {
    // file-shape: render the name
    const name = (v as { name?: string; file_name?: string }).name
      ?? (v as { file_name?: string }).file_name;
    if (typeof name === "string") return name;
    return JSON.stringify(v);
  }
  return String(v);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
